/**
 * QuickAccessHandle.tsx
 *
 * Small pill at the top-center of the page.
 * Expands on hover to reveal the Quick Access menu.
 * Collapses back when the mouse leaves.
 * Ctrl+Q toggles the expanded state.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { Shield, Lock, LogOut, KeyRound, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useVault } from '@/base/contexts/VaultContext';
import { useTotpEntries, useTotpBatchCodes } from '@/base/hooks/useTotpEntries';
import { TotpQuickViewCard } from './TotpQuickViewCard';
import { cn } from '@/base/lib/utils';
import { Button } from '@/base/components/ui/button';
import { apiClient } from '@/base/utils/api';

export function QuickAccessHandle() {
  const navigate = useNavigate();
  const vault = useVault();
  const [expanded, setExpanded] = useState(false);
  const [pinned, setPinned] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const collapseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isOpen = expanded || pinned;

  // Fetch TOTP entries
  const { data: totpEntries = [] } = useTotpEntries();
  const favoriteEntries = totpEntries.filter((e) => e.isFavorite && !e.isDisabled).slice(0, 5);
  const favoriteIds = favoriteEntries.map((e) => e.id);
  const { data: batchCodes } = useTotpBatchCodes(favoriteIds, vault.isUnlocked && favoriteIds.length > 0);

  // Keyboard shortcut: Ctrl+Q to toggle pinned state
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === 'q') {
        e.preventDefault();
        setPinned((prev) => !prev);
      }
      if (e.key === 'Escape' && pinned) {
        e.preventDefault();
        setPinned(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [pinned]);

  // Hover handlers with small delay to prevent flicker
  const handleMouseEnter = useCallback(() => {
    if (collapseTimerRef.current) {
      clearTimeout(collapseTimerRef.current);
      collapseTimerRef.current = null;
    }
    setExpanded(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (pinned) return;
    collapseTimerRef.current = setTimeout(() => {
      setExpanded(false);
    }, 200);
  }, [pinned]);

  // Cleanup timer
  useEffect(() => {
    return () => {
      if (collapseTimerRef.current) clearTimeout(collapseTimerRef.current);
    };
  }, []);

  // Click outside to unpin
  useEffect(() => {
    if (!pinned) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setPinned(false);
        setExpanded(false);
      }
    };

    // Small delay to prevent the opening click from immediately closing
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 50);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [pinned]);

  // Navigation handlers
  const handleNavigate = useCallback(
    (path: string) => {
      setPinned(false);
      setExpanded(false);
      navigate(path);
    },
    [navigate]
  );

  const handleExtendVault = useCallback(async () => {
    try {
      await apiClient.post('/v1/cvs/vault/extend');
      vault.refetchStatus();
    } catch (error) {
      console.error('Failed to extend vault:', error);
    }
  }, [vault]);

  const handleQuickLock = useCallback(async () => {
    await vault.lockVault();
    setPinned(false);
    setExpanded(false);
  }, [vault]);

  const handleLogOut = useCallback(async () => {
    await vault.lockAndSignOut();
    setPinned(false);
    setExpanded(false);
    navigate('/login');
  }, [vault, navigate]);

  const formatTTL = (seconds: number | null) => {
    if (seconds === null) return 'N/A';
    const minutes = Math.floor(seconds / 60);
    return `${minutes} min`;
  };

  return (
    <div
      ref={containerRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className="fixed top-0 left-1/2 -translate-x-1/2 z-50 w-[420px]"
      style={{ pointerEvents: isOpen ? 'auto' : 'none' }}
    >
      {/* Collapsed Pill — always centered, acts as hover target */}
      <div
        className="flex justify-center"
        style={{ pointerEvents: 'auto' }}
      >
        <div
          className={cn(
            'transition-all duration-200 ease-out',
            'bg-[var(--grok-surface-1)] border border-t-0 border-[var(--grok-border)]',
            'rounded-b-lg cursor-pointer',
            isOpen
              ? 'w-[100px] h-0 opacity-0'
              : 'w-[100px] h-[10px] opacity-100 hover:h-[14px] hover:bg-[var(--grok-surface-2)]'
          )}
          onClick={() => setPinned(true)}
          aria-label="Quick Access (Ctrl+Q)"
          role="button"
          tabIndex={0}
        />
      </div>

      {/* Expanded Menu — uses transform for smooth GPU-accelerated animation */}
      <div
        className={cn(
          'origin-top',
          'bg-[var(--grok-surface-1)] border border-t-0 border-[var(--grok-border)]',
          'rounded-b-xl overflow-hidden',
          isOpen ? 'shadow-xl' : ''
        )}
        style={{
          transform: isOpen ? 'scaleY(1)' : 'scaleY(0)',
          opacity: isOpen ? 1 : 0,
          transition: 'transform 200ms cubic-bezier(0.4, 0, 0.2, 1), opacity 150ms ease-out',
        }}
      >
        {/* Quick Actions */}
        <div className="px-4 py-3 border-b border-[var(--grok-border)]">
          <div className="flex flex-wrap gap-1.5">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleNavigate('/credentials')}
              className="flex items-center gap-1.5 text-xs h-7"
            >
              <Shield className="w-3.5 h-3.5" />
              CVS
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleNavigate('/credentials/totp')}
              className="flex items-center gap-1.5 text-xs h-7"
            >
              <KeyRound className="w-3.5 h-3.5" />
              TOTP
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleQuickLock}
              disabled={!vault.isUnlocked}
              className="flex items-center gap-1.5 text-xs h-7"
            >
              <Lock className="w-3.5 h-3.5" />
              Lock
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogOut}
              className="flex items-center gap-1.5 text-xs h-7"
            >
              <LogOut className="w-3.5 h-3.5" />
              Out
            </Button>
          </div>
        </div>

        {/* TOTP Quick View */}
        {vault.isUnlocked && favoriteEntries.length > 0 && (
          <div className="px-4 py-3 border-b border-[var(--grok-border)]">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-semibold text-[var(--grok-text-primary)]">
                TOTP Quick View
              </h3>
              <button
                onClick={() => handleNavigate('/credentials/totp')}
                className="text-xs text-[var(--ems-primary)] hover:underline"
              >
                Manage
              </button>
            </div>
            <div className="space-y-1.5">
              {favoriteEntries.map((entry) => (
                <TotpQuickViewCard
                  key={entry.id}
                  entry={entry}
                  code={batchCodes?.codes[entry.id]}
                />
              ))}
            </div>
          </div>
        )}

        {/* Vault Status */}
        <div className="px-4 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <span
                className={cn(
                  'w-1.5 h-1.5 rounded-full',
                  vault.isUnlocked ? 'bg-[var(--ems-success)]' : 'bg-[var(--grok-text-muted)]'
                )}
              />
              <span className="text-xs text-[var(--grok-text-secondary)]">
                Vault: {vault.isUnlocked ? 'Unlocked' : 'Locked'}
                {vault.isUnlocked && vault.ttlRemaining && (
                  <span className="ml-1 text-[var(--grok-text-muted)]">
                    ({formatTTL(vault.ttlRemaining)})
                  </span>
                )}
              </span>
            </div>
            {vault.isUnlocked && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleExtendVault}
                className="flex items-center gap-1 text-xs h-6 px-2"
              >
                <RefreshCw className="w-3 h-3" />
                Extend
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
